// A task group's facts: who owns it, what it runs, where its tasks come from (a Jira source's JQL),
// how often and how well the poller refreshes it, the repositories its workspace clones, its
// parallelism and isolation. Its icon bar (S128) heads the panel, and the panel folds to that bar,
// remembered per browser; folded, the bar keeps the source and when it was last polled.
import { CaretDown } from "@phosphor-icons/react";

import { ago, shortId } from "../../pages/format";
import { cardClass, Field } from "../../ui/Card";
import { cn } from "../../ui/cn";
import { ErrorState } from "../../ui/states";
import { Mono } from "../../ui/status";
import { dayTime } from "../../workloads/format";
import { cadenceLabel, sourceDetail } from "../display";
import { IsolationBadge, WorkflowVersion } from "../list/TaskGroupsTable";
import type { Lookups } from "../lookups";
import type { TaskGroup } from "../model";
import { useT } from "../../i18n";
import { GroupVpnLine } from "../../vpn/GroupVpnLine";
import { GroupToolbar } from "./GroupToolbar";
import { useGroupPanelOpen } from "./panelState";

const FACTS_ID = "group-facts";

export function GroupSummary({ group, lookups }: { group: TaskGroup; lookups: Lookups }) {
  const { tasks, common, vpn } = useT();
  const t = tasks.group;
  const c = tasks.columns;
  const detail = sourceDetail(group);
  const [open, setOpen] = useGroupPanelOpen();
  const polled = group.last_polled_at ? t.polled(ago(group.last_polled_at)) : t.notPolled;
  return (
    <div className="space-y-4">
      {group.last_poll_error ? <ErrorState title={t.lastPollFailed} message={group.last_poll_error} /> : null}
      <section className={cardClass} aria-label={t.factsLabel}>
        <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2", open && "border-b border-muted")}>
          <GroupToolbar group={group} />
          {!open && group.source !== "manual" ? (
            <span className="min-w-0 truncate text-[13px] text-gray-500" data-testid="group-folded-poll">
              {tasks.source[group.source]} · {polled}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-controls={FACTS_ID}
            className="ms-auto inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-1000 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {open ? t.toolbar.hideDetails : t.toolbar.showDetails}
            <CaretDown weight="bold" className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} aria-hidden />
          </button>
        </div>
        {open ? (
          <div id={FACTS_ID} className="grid grid-cols-2 gap-x-6 gap-y-4 p-5 md:grid-cols-4">
            <Field label={c.owner}>{lookups.owner(group.owner_user_id)}</Field>
            <Field label={c.taskType}>{lookups.taskType(group.task_type_id)}</Field>
            <Field label={c.workflow}>
              <WorkflowVersion lookups={lookups} versionId={group.workflow_version_id} />
            </Field>
            <Field label={t.isolation}>
              <IsolationBadge isolation={group.isolation} />
            </Field>
            <Field label={t.source}>
              <span title={detail || undefined}>
                {tasks.source[group.source]}
                {detail ? <span className="font-normal text-gray-500"> · {detail}</span> : null}
              </span>
            </Field>
            <Field label={t.refresh}>{group.source === "manual" ? "—" : `${cadenceLabel(group.poll_minutes)} · ${polled}`}</Field>
            <Field label={t.repositories}>
              {group.repositories.length === 0 ? (
                <span className="font-normal text-gray-500">{group.source === "github" ? t.sourceOwn : common.states.none}</span>
              ) : (
                <span className="flex flex-col gap-0.5">
                  {group.repositories.map((r) => (
                    <Mono key={r.repo} className="truncate text-[12.5px]">
                      {r.repo}
                      <span className="text-gray-500"> @ {r.default_branch ?? "main"}</span>
                    </Mono>
                  ))}
                </span>
              )}
            </Field>
            <Field label={t.parallel}>{group.max_parallel_tasks}</Field>
            <Field label={t.environment} mono>
              {group.environment_id ? <span title={group.environment_id}>{shortId(group.environment_id)}</span> : t.notAssigned}
            </Field>
            <Field label={vpn.title}>
              <GroupVpnLine group={group} />
            </Field>
            <Field label={t.created}>{dayTime(group.created_at)}</Field>
            <Field label={c.updated}>{ago(group.updated_at)}</Field>
          </div>
        ) : null}
      </section>
    </div>
  );
}
